import React, { useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getExpenseTableConfig } from "@/config/tableConfigs/expenseTableConfig";
import { expenseService } from "@/services/expenseService";
import { billingItemService } from "@/services/billingItemService";
import { payrollItemService } from "@/services/payrollItemService";
import { ExpenseType } from "@/types/db/expenses";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { FromDB } from "@/types";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteExpenseModal } from "@/components/Modals/DeleteExpenseModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function ExpensesPage() {
  const { userRoles, displayNames } = useAuth();
  const toast = useToast();
  const { triggerRefresh, clearEntitySelection } = useRowSelectionContext();
  useSetPageTitle(displayNames?.expenses?.plural);

  const tableConfig = getExpenseTableConfig(userRoles, displayNames);

  const headerActions = useMemo(
    () => [
      {
        text: "Copy to Billing",
        icon: "cash-register" as const,
        onPress: async ({ selectedIds, selectedMap }: any) => {
          try {
            // Create billing items from selected expenses
            const billingItems = selectedIds.map((id: string) => {
              const expense = selectedMap[id];

              // Extract client_id, expense_id, and amount
              const clientId = expense?.client_id;
              const expenseId = expense.id;
              const amount = expense?.amount;

              if (!clientId) {
                console.warn(`No client_id found for expense ${id}`);
              }

              return {
                client_id: clientId,
                expense_id: expenseId,
                amount: amount,
                description: expense?.description || undefined,
                shift_id: expense?.shift_id || undefined,
                project_id: expense?.project_id || undefined,
                billing_items: expense?.billing_items || undefined,
                user_id: expense?.user_id || undefined,
                quantity: 1,
                unit: "-",
              };
            });

            // Filter out items without client_id
            const validBillingItems = billingItems.filter((item: any) => item.client_id && !item.billing_items);

            if (validBillingItems.length === 0) {
              toast.showToast({
                title: "No valid expenses",
                message: "Selected expenses are missing client information or already have billing items.",
                toastType: "error",
              });
              return;
            }

            const result = await billingItemService.createBillingItemsFromExpenses(validBillingItems);

            // Link billing item IDs back to expense meta_data
            // Build a map of expense_id -> billing_item_id from the results
            const expenseBillingMap: Record<string, string[]> = {};
            result.forEach((billingItem: any) => {
              const expenseId = billingItem.meta_data?.links?.expenses?.[0];
              if (expenseId) {
                if (!expenseBillingMap[expenseId]) expenseBillingMap[expenseId] = [];
                expenseBillingMap[expenseId].push(billingItem.id);
              }
            });

            const expenseIdsToUpdate = Object.keys(expenseBillingMap);
            if (expenseIdsToUpdate.length > 0) {
              // Fetch current meta_data from DB to avoid overwriting existing fields
              const currentExpenses = await Promise.all(
                expenseIdsToUpdate.map((id) => expenseService.getExpenseById(id)),
              );

              const expenseUpdates = currentExpenses.map((expense) => {
                const currentMeta = expense.meta_data || {};
                const existingLinks = currentMeta.links || {};
                return {
                  id: expense.id,
                  meta_data: {
                    ...currentMeta,
                    links: {
                      ...existingLinks,
                      billing_items: [...(existingLinks.billing_items || []), ...expenseBillingMap[expense.id]],
                    },
                  },
                };
              });

              await expenseService.saveRowPatches(expenseUpdates);
            }

            toast.showToast({
              title: `Created ${result.length} billing item${result.length === 1 ? "" : "s"}`,
              toastType: "success",
            });
            triggerRefresh("billing_item");
            triggerRefresh("expense");
            clearEntitySelection("expense");
          } catch (error: any) {
            toast.showToast({
              title: "Failed to copy to billing",
              message: error.message,
              toastType: "error",
            });
          }
        },
      },
    ],
    [toast, triggerRefresh, clearEntitySelection],
  );

  // Check if user has expenses read permission
  const hasExpensesAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.expenses?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<ExpenseType>> = useMemo(
    () => ({
      storageKey: "expense_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteExpenseModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("expense_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [billingItemService, payrollItemService],
  );

  if (!hasExpensesAccess) {
    return <NoPermissionScreen entityName={displayNames?.expenses?.plural || "Expenses"} />;
  }

  return (
    <EntityTablePage<FromDB<ExpenseType>>
      {...tableConfig}
      fetchData={expenseService.getExpenses}
      updateEntities={expenseService.saveRowPatches}
      deleteEntities={expenseService.deleteExpensesByIds}
      tableConfig={{
        ...tableConfig.tableConfig,
        headerActions,
      }}
      selectionActions={({ selectedIds, selectedMap }) => [
        {
          id: "copy-billing",
          label: "Copy to Billing",
          icon: "cash-register",
          onPress: () => headerActions[0].onPress({ selectedIds, selectedMap }),
        },
      ]}
      deleteConfirmation={deleteConfirmation}
    />
  );
}
